import React, { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Activity, CheckCircle2, XCircle, Clock, Camera, FileText, Video, BookOpen, Radio, BarChart3 } from 'lucide-react';

const STATUS_COLOURS = {
  planned:   '#3B82F6',
  completed: '#10B981',
  missed:    '#E63946',
  rejected:  '#6B7280',
};

const TYPE_META = {
  livetrade:        { label: 'Live Trade',        icon: Radio,     colour: '#E63946' },
  livesession:      { label: 'Live Session',      icon: Video,     colour: '#3B82F6' },
  webinar:          { label: 'Webinar/Class',     icon: BookOpen,  colour: '#10B981' },
  shooting_content: { label: 'Shooting Content',  icon: Camera,    colour: '#F59E0B' },
  publish_content:  { label: 'Publish Content',   icon: FileText,  colour: '#A855F7' },
  shooting:         { label: 'Shooting Content',  icon: Camera,    colour: '#F59E0B' }, // legacy alias
  signal:           { label: 'Signal',             icon: BarChart3, colour: '#8B5CF6' },
  other:            { label: 'Other',              icon: FileText,  colour: '#6B7280' },
};

const normaliseType = (t) => (t === 'shooting' ? 'shooting_content' : (t || 'other'));

export default function ActivitySummaryReportModal({ open, onOpenChange, activities = [], channelType = 'public', currentDate = new Date(), ib }) {
  // Filter activities to the currently-viewed month + active channel toggle
  const monthly = useMemo(() => {
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    return (activities || []).filter((a) => {
      if (!a || a.channel !== channelType) return false;
      const d = new Date(a.date);
      if (Number.isNaN(d.getTime())) return false;
      return d.getFullYear() === y && d.getMonth() === m;
    });
  }, [activities, channelType, currentDate]);

  const stats = useMemo(() => {
    const byType = {};
    const byStatus = { planned: 0, completed: 0, missed: 0, rejected: 0 };
    let shootingQty = 0;
    let publishQty = 0;
    let shootingDone = 0;
    let publishDone = 0;
    for (const a of monthly) {
      const t = normaliseType(a.type);
      byType[t] = (byType[t] || 0) + 1;
      byStatus[a.status || 'planned'] = (byStatus[a.status || 'planned'] || 0) + 1;
      const qty = Number(a.quantity || 0);
      if (t === 'shooting_content') {
        shootingQty += qty;
        if (a.status === 'completed') shootingDone += qty;
      } else if (t === 'publish_content') {
        publishQty += qty;
        if (a.status === 'completed') publishDone += qty;
      }
    }
    return { byType, byStatus, shootingQty, publishQty, shootingDone, publishDone };
  }, [monthly]);

  const typeChartData = Object.entries(stats.byType).map(([k, v]) => ({
    name: TYPE_META[k]?.label || k,
    count: v,
    fill: TYPE_META[k]?.colour || '#6B7280',
  }));

  const statusChartData = Object.entries(stats.byStatus)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: v, fill: STATUS_COLOURS[k] }));

  const total = monthly.length;
  const completedCount = stats.byStatus.completed || 0;
  const completionRate = total > 0 ? Math.round((completedCount / total) * 100) : 0;

  const monthLabel = currentDate.toLocaleDateString('en-MY', { month: 'long', year: 'numeric' });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="summary-report-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Activity size={18} className="text-cyan-400" />
            Activity Summary — {monthLabel}
          </DialogTitle>
          <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--text-muted))]">
            {ib?.name || 'Channel'} • {channelType.toUpperCase()} • {total} activities
          </div>
        </DialogHeader>

        {/* Stat strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <div className="p-3 rounded-lg bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]">
            <div className="flex items-center gap-1.5 mb-1">
              <Clock size={11} className="text-[#3B82F6]" />
              <span className="text-[9px] uppercase tracking-wider text-[#6B7280] font-bold">Total</span>
            </div>
            <p className="text-xl font-bold text-white" data-testid="summary-total">{total}</p>
          </div>
          <div className="p-3 rounded-lg bg-[#10B981]/5 border border-[#10B981]/30">
            <div className="flex items-center gap-1.5 mb-1">
              <CheckCircle2 size={11} className="text-[#10B981]" />
              <span className="text-[9px] uppercase tracking-wider text-[#10B981] font-bold">Completed</span>
            </div>
            <p className="text-xl font-bold text-emerald-400" data-testid="summary-completed">{completedCount}</p>
            <p className="text-[10px] text-[hsl(var(--text-muted))]">{completionRate}% rate</p>
          </div>
          <div className="p-3 rounded-lg bg-[#F59E0B]/5 border border-[#F59E0B]/30">
            <div className="flex items-center gap-1.5 mb-1">
              <Camera size={11} className="text-[#F59E0B]" />
              <span className="text-[9px] uppercase tracking-wider text-[#F59E0B] font-bold">Shooting (VID-ORG)</span>
            </div>
            <p className="text-xl font-bold text-amber-300" data-testid="summary-shooting">{stats.shootingDone}</p>
            <p className="text-[10px] text-[hsl(var(--text-muted))]">of {stats.shootingQty} planned</p>
          </div>
          <div className="p-3 rounded-lg bg-[#A855F7]/5 border border-[#A855F7]/30">
            <div className="flex items-center gap-1.5 mb-1">
              <FileText size={11} className="text-[#A855F7]" />
              <span className="text-[9px] uppercase tracking-wider text-[#A855F7] font-bold">Publish</span>
            </div>
            <p className="text-xl font-bold text-purple-300" data-testid="summary-publish">{stats.publishDone}</p>
            <p className="text-[10px] text-[hsl(var(--text-muted))]">of {stats.publishQty} planned</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {/* Activity count by type */}
          <div className="p-4 rounded-lg bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]">
            <p className="text-xs uppercase tracking-wider text-[#9CA3AF] font-medium mb-3">Activity Count by Type</p>
            <div className="h-56" data-testid="summary-type-chart">
              {typeChartData.length === 0 ? (
                <p className="text-xs text-[hsl(var(--text-muted))] text-center pt-16">No activities this month</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={typeChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                    <XAxis dataKey="name" stroke="#6B7280" fontSize={10} angle={-15} textAnchor="end" height={50} interval={0} />
                    <YAxis stroke="#6B7280" fontSize={10} allowDecimals={false} />
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {typeChartData.map((entry, idx) => (
                        <Cell key={`c-${idx}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Status donut */}
          <div className="p-4 rounded-lg bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]">
            <p className="text-xs uppercase tracking-wider text-[#9CA3AF] font-medium mb-3">Status Breakdown</p>
            <div className="h-56" data-testid="summary-status-chart">
              {statusChartData.length === 0 ? (
                <p className="text-xs text-[hsl(var(--text-muted))] text-center pt-16">No data</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={statusChartData} dataKey="value" innerRadius={45} outerRadius={75} paddingAngle={3}>
                      {statusChartData.map((entry, idx) => (
                        <Cell key={`s-${idx}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        <p className="text-[10px] text-[hsl(var(--text-muted))] mt-3 text-center">
          Summary dikira dari activities yang ada dalam bulan {monthLabel} untuk channel{' '}
          <span className="font-bold uppercase tracking-wider" style={{ color: channelType === 'vip' ? '#F59E0B' : '#3B82F6' }}>{channelType}</span>.
          Shooting Content yang status = Done akan auto-reflect dalam <span className="text-amber-300">Total Video (Organic)</span> di Channel Scorecard.
        </p>
      </DialogContent>
    </Dialog>
  );
}
